/**
 * useViewSeen — records when a view is visited and provides "last seen" data.
 *
 * Placed in ViewShell (runs once per view navigation).
 *
 * On mount / view change:
 *   1. Reads the PREVIOUS lastSeen timestamp (before marking)
 *   2. Marks the view as seen NOW
 *   3. Returns the previous timestamp (so banners/dots can compare)
 *
 * This two-step ensures the banner shows "N new tasks since [time]"
 * referring to the LAST visit, not the current one.
 */

import { useEffect, useRef } from "react"
import {
  useViewSeenStore,
  viewSeenKey,
} from "@/features/views/stores/viewSeenStore"
import type { ViewId } from "@/features/views/viewRegistry"

/**
 * Mark the current view as "seen" and return the previous lastSeen timestamp.
 *
 * @param viewId     The view identifier (e.g. "today", "project")
 * @param containerId Optional container ID for dynamic views
 * @returns The PREVIOUS lastSeen timestamp (before this visit), or undefined if never seen
 */
export function useViewSeen(
  viewId: ViewId,
  containerId?: string | null,
): { lastSeenAt: string | undefined; viewOpenedAt: string } {
  const key = viewSeenKey(viewId, containerId)
  const hydrated = useViewSeenStore((s) => s.hydrated)
  const markSeen = useViewSeenStore((s) => s.markSeen)
  const getLastSeen = useViewSeenStore((s) => s.getLastSeen)

  // Capture the previous timestamp and session start before marking.
  // Use refs so we only capture them once per key change.
  const prevTimestampRef = useRef<string | undefined>(undefined)
  const viewOpenedAtRef = useRef<string>(String(Date.now()))
  const prevKeyRef = useRef<string>("")

  if (hydrated && prevKeyRef.current !== key) {
    prevTimestampRef.current = getLastSeen(key)
    viewOpenedAtRef.current = String(Date.now())
    prevKeyRef.current = key
  }

  useEffect(() => {
    if (!hydrated) return
    // Mark the view as seen after a short delay to avoid marking
    // during quick navigations (e.g. scrolling through sidebar)
    const timer = setTimeout(() => {
      markSeen(key)
    }, 1000)

    return () => clearTimeout(timer)
  }, [key, hydrated, markSeen])

  return {
    lastSeenAt: prevTimestampRef.current,
    viewOpenedAt: viewOpenedAtRef.current,
  }
}
